import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Lenis from 'lenis';
import Marquee from 'react-fast-marquee';
import { MonkeyLogo } from '../components/MonkeyLogo';
import { api } from '../utils/api';
import {
  FaInstagram, FaTiktok, FaYoutube, FaXTwitter, FaSpotify,
  FaTwitch, FaDiscord, FaSoundcloud, FaPatreon, FaTelegram, FaSnapchat
} from 'react-icons/fa6';
import { SiApplemusic } from 'react-icons/si';
import {
  ArrowRight, ArrowUpRight, BarChart3, Palette, Music2, Sparkles,
  Heart, QrCode, Layers, Check
} from 'lucide-react';
import { ComparisonTable } from '../components/seo/ComparisonTable';
import { FaqSection } from '../components/seo/FaqSection';
import { LINKTREE_ROWS } from '../data/seoPages';

/* ---------- Design assets from /app/design_guidelines.json ---------- */
const CREATOR_CUTOUT = 'https://static.prod-images.emergentagent.com/jobs/e4fc18b8-6f91-4eff-a50b-31c7d916e998/images/76df445fd73ee14a6f2cc7d81bfe3d2a27eb9e947b0a61ec6086bae9e8a8fba1.png';
const PRICING_STAGE = 'https://static.prod-images.emergentagent.com/jobs/e4fc18b8-6f91-4eff-a50b-31c7d916e998/images/79cd5ce7282fdb6e5f79cf909350fc9d34e6bfa4c0db80e92bd7d80dd9c35f10.png';
const MASCOT_3D = 'https://static.prod-images.emergentagent.com/jobs/e4fc18b8-6f91-4eff-a50b-31c7d916e998/images/38cf445d015eb5d5050dd20bc45c69245f2f082344b72fad814e00bd48e35365.png';
const MASCOT_HERO = '/mascot-hero-transparent.png';
const MUSICIAN = 'https://images.unsplash.com/photo-1771894429175-c0b6a809e30f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxtdXNpY2lhbiUyMHBvcnRyYWl0JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc5MjA0MTc3fDA&ixlib=rb-4.1.0&q=85';
const FASHION = 'https://images.pexels.com/photos/12433022/pexels-photo-12433022.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

/* ---------- Typography classes ---------- */
/* Cinematic Cyber-Utility typography stack (per /app/design_guidelines.json):
   - Outfit for display headings (metallic shine via text-clip gradient)
   - Manrope for body / UI copy */
const DISPLAY = "font-display font-extrabold tracking-tighter";
const BODY = "font-body";
const METALLIC = "text-transparent bg-clip-text bg-gradient-to-br from-gray-900 via-gray-600 to-gray-900";
const METALLIC_DARKBG = "text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/40";

/* ---------- Motion easings ---------- */
const EASE_OUT = [0.16, 1, 0.3, 1];

const useLenis = () => {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    let rafId;
    const raf = (t) => { lenis.raf(t); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);
};

/* ---------- Reveal helper (below-fold). Above-fold uses initial={false} ---------- */
const Reveal = ({ children, delay = 0, y = 24, className = '', immediate = false }) => (
  <motion.div
    initial={immediate ? false : { opacity: 0, y }}
    animate={immediate ? { opacity: 1, y: 0 } : undefined}
    whileInView={immediate ? undefined : { opacity: 1, y: 0 }}
    viewport={immediate ? undefined : { once: true, margin: '-80px' }}
    transition={{ duration: 0.9, ease: EASE_OUT, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ---------- HERO with monkey on stage + orbiting social icons ---------- */
const ORBIT_ICONS = [
  { Icon: FaSpotify,    label: 'Spotify',     color: '#1DB954', angle: -90,  radius: 1.10 },
  { Icon: FaInstagram,  label: 'Instagram',   color: '#E4405F', angle: -54,  radius: 1.18 },
  { Icon: FaYoutube,    label: 'YouTube',     color: '#FF0000', angle: -18,  radius: 1.10 },
  { Icon: FaTiktok,     label: 'TikTok',      color: '#000000', angle:  18,  radius: 1.18 },
  { Icon: FaXTwitter,   label: 'X (Twitter)', color: '#000000', angle:  54,  radius: 1.10 },
  { Icon: FaPatreon,    label: 'Patreon',     color: '#FF424D', angle:  90,  radius: 1.18 },
  { Icon: FaTwitch,     label: 'Twitch',      color: '#9146FF', angle: 126,  radius: 1.10 },
  { Icon: FaDiscord,    label: 'Discord',     color: '#5865F2', angle: 162,  radius: 1.18 },
  { Icon: FaSoundcloud, label: 'SoundCloud',  color: '#FF5500', angle: 198,  radius: 1.10 },
  { Icon: SiApplemusic, label: 'Apple Music', color: '#FA243C', angle: 234,  radius: 1.18 },
];

/* Each icon: pos is polar angle. We animate `rotation` (the parent group) clockwise
   and counter-rotate each icon so it stays upright while orbiting. */
const OrbitIcon = ({ Icon, label, color, angle, radius, index }) => {
  const rad = (angle * Math.PI) / 180;
  const left = 50 + Math.cos(rad) * 38 * radius;
  const top = 50 + Math.sin(rad) * 38 * radius;
  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{ left: `${left}%`, top: `${top}%`, transform: 'translate(-50%, -50%)' }}
      initial={{ opacity: 0, scale: 0.3 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, delay: 0.5 + index * 0.07, ease: EASE_OUT }}
    >
      <div className="relative">
        <Icon aria-label={label} role="img" className="w-9 h-9 md:w-11 md:h-11 relative z-10" style={{ color, filter: `drop-shadow(0 6px 20px ${color}aa)` }} />
        <div
          className="absolute -inset-4 -z-10 pointer-events-none"
          style={{ background: `radial-gradient(closest-side, ${color}55 0%, transparent 70%)` }}
        />
      </div>
    </motion.div>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const orbitBoxRef = useRef(null);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden bg-[#D9E9FC]" data-testid="landing-hero">
      {/* Soft blue radial wash behind the stage */}
      <div
        className="absolute inset-0 -z-20 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 80% 60% at 70% 50%, rgba(0,85,255,0.10) 0%, rgba(0,85,255,0.03) 30%, transparent 65%)' }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-28 md:pt-36 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        {/* LEFT: copy */}
        <div className="relative z-10 max-w-xl">
          <Reveal immediate>
            <Link to="/pricing"
              className={`${BODY} inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gray-600 bg-white border border-gray-200 rounded-full px-3.5 py-1.5 mb-8 hover:border-gray-300 hover:text-gray-900 transition-colors shadow-sm`}
              data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] shadow-[0_0_8px_rgba(16,185,129,0.8)]" />
              Free plan · Pro from €3.33/mo
              <ArrowRight className="w-3 h-3" />
            </Link>
          </Reveal>

          <h1 className={`${DISPLAY} text-[clamp(2.6rem,7.5vw,6.2rem)] leading-[0.92] mb-7`}>
            <Reveal immediate delay={0.05}>
              <span className={`block ${METALLIC}`}>A better link in bio.</span>
            </Reveal>
            <Reveal immediate delay={0.15}>
              <span className="block font-light italic tracking-tight text-[#0055FF]">Without the extra fees.</span>
            </Reveal>
          </h1>

          <Reveal immediate delay={0.25}>
            <p className={`${BODY} text-gray-600 text-lg leading-relaxed mb-8 max-w-md font-normal`}>
              Own your stage: one beautiful creator page with unlimited links, rich embeds,
              real brand icons, analytics, QR codes, fan pages and a tip jar —
              free to start, Pro from €3.33/mo.
            </p>
          </Reveal>

          <Reveal immediate delay={0.35}>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-7">
              <Link to="/signup" className="group">
                <button
                  className={`${BODY} w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white text-sm font-semibold px-7 py-4 rounded-full hover:bg-[#3377FF] transition-all shadow-[0_10px_40px_rgba(0,85,255,0.45)] hover:shadow-[0_15px_60px_rgba(0,85,255,0.7)] hover:scale-[1.02]`}
                  data-testid="hero-signup-btn">
                  Create your free page
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </Link>
              <Link to="/examples">
                <button
                  className={`${BODY} w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm font-semibold text-gray-900 px-6 py-3.5 rounded-full border border-gray-200 bg-white hover:bg-[#EAF2FF] hover:border-gray-300 shadow-sm transition-colors`}
                  data-testid="hero-examples-btn">
                  See examples
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </Reveal>

          <Reveal immediate delay={0.45}>
            <div className="flex flex-wrap gap-2" data-testid="hero-trust-chips">
              {['Free plan forever', '63 brand icons', 'Tip jar — 0% commission', 'Analytics included'].map((t) => (
                <span key={t} className={`${BODY} inline-flex items-center gap-1.5 text-[11px] text-gray-600 px-3 py-1.5 rounded-full border border-gray-200 bg-white shadow-sm`}>
                  <Check className="w-3 h-3 text-emerald-500" /> {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        {/* RIGHT: monkey at centre with clockwise orbiting icons */}
        <div ref={orbitBoxRef} className="relative z-10 mx-auto w-full max-w-[600px] aspect-square">
          {/* Faint orbit rings (subtle reference circles) */}
          <div className="absolute inset-[8%] rounded-full border border-gray-950/[0.06] pointer-events-none" />
          <div className="absolute inset-[18%] rounded-full border border-gray-950/[0.05] pointer-events-none" />

          {/* Spotlight glow under monkey — radial gradient instead of bg+blur,
              because blur-3xl mis-renders on mobile Safari (exposes the underlying
              square as a visible "box"). */}
          <div
            className="absolute left-1/2 top-[56%] -translate-x-1/2 -translate-y-1/2 w-[80%] aspect-square pointer-events-none"
            style={{ background: 'radial-gradient(closest-side, rgba(0,85,255,0.18) 0%, rgba(0,85,255,0.08) 35%, transparent 70%)' }}
          />

          {/* Monkey mascot — visible BODY centred at the orbit ring centre.
              Slight downward nudge so the visible torso (rather than the
              image bbox) lands at orbit ring centre. */}
          <div className="absolute left-1/2 top-[56%] -translate-x-1/2 -translate-y-1/2 w-[44%] z-20">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, ease: EASE_OUT }}
            >
              <img
                src={MASCOT_HERO}
                alt="LinkMonkey mascot"
                className="w-full h-auto drop-shadow-[0_30px_60px_rgba(0,85,255,0.55)] select-none pointer-events-none"
                draggable={false}
              />
            </motion.div>
          </div>

          {/* Orbiting brand icons — continuous clockwise rotation; icons counter-rotate to stay upright */}
          {/* Static orbiting brand icons — no rotation, no bounce. */}
          <div className="absolute inset-0 z-30">
            {ORBIT_ICONS.map((cfg, i) => (
              <OrbitIcon key={i} index={i} {...cfg} />
            ))}
          </div>
        </div>
      </div>

      {/* Scroll cue — hidden on mobile to avoid overlapping the orbit */}
      <div className="hidden lg:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-gray-400">
        <span className={`${BODY} text-[10px] uppercase tracking-[0.3em]`}>Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-gray-400 to-transparent" />
      </div>
    </section>
  );
};

/* ---------- MARQUEE strip ---------- */
const KineticMarquee = () => (
  <section className="relative py-10 md:py-14 border-y border-blue-100 bg-[#F1F7FF] overflow-hidden" data-testid="landing-marquee">
    <Marquee gradient gradientColor="#FFFFFF" gradientWidth={120} speed={40} pauseOnHover>
      {['Musicians', 'Photographers', 'Streamers', 'Designers', 'Models', 'Coaches', 'Producers', 'Writers'].map((label, i) => (
        <span key={i} className={`${DISPLAY} text-[clamp(3rem,8vw,7rem)] leading-none text-transparent mx-8`}
          style={{ WebkitTextStroke: '1px rgba(10,10,10,0.16)' }}>
          {label} <span className="text-gray-200 px-3">✦</span>
        </span>
      ))}
    </Marquee>
  </section>
);

/* ---------- BENTO features ---------- */
const BentoCard = ({ children, className = '', span = 'col-span-12 md:col-span-6', testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.9, ease: EASE_OUT }}
    className={`${span} group relative rounded-3xl border border-gray-200 bg-white overflow-hidden hover:border-gray-300 hover:shadow-[0_10px_25px_-5px_rgba(0,0,0,0.06)] transition-[border-color,box-shadow] ${className}`}
    data-testid={testid}
  >
    {children}
  </motion.div>
);

const Features = () => (
  <section className="relative py-28 md:py-40 px-6 md:px-10 bg-[#D9E9FC]" data-testid="landing-features">
    {/* subtle noise */}
    <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

    <div className="relative max-w-7xl mx-auto">
      <Reveal>
        <p className={`${BODY} text-[11px] uppercase tracking-[0.25em] text-gray-500 mb-4`}>The toolkit</p>
        <h2 className={`${DISPLAY} text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.95] mb-20 max-w-4xl`}>
          <span className={METALLIC}>Everything you'd expect.</span><br />
          <span className="text-gray-500">Plus the things others charge for.</span>
        </h2>
      </Reveal>

      <div className="grid grid-cols-12 gap-5">
        {/* Live Preview — wide */}
        <BentoCard span="col-span-12 lg:col-span-8" testid="bento-preview">
          <div className="p-7 md:p-10">
            <Layers className="w-5 h-5 text-gray-500 mb-4" strokeWidth={1.5} />
            <h3 className={`${DISPLAY} text-gray-900 text-3xl mb-2 tracking-tight`}>Live preview</h3>
            <p className={`${BODY} text-gray-600 text-sm leading-relaxed max-w-md mb-6`}>Edit on the left, watch it render in real-time on the right. No deploy. No refresh.</p>
          </div>
          <div className="relative h-[300px] md:h-[360px] mx-7 md:mx-10 mb-7 md:mb-10 rounded-2xl overflow-hidden border border-white/10">
            <img src={MUSICIAN} alt="" className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-[1200ms]" />
            <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/60 to-transparent" />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: EASE_OUT, delay: 0.2 }}
              className="absolute bottom-5 right-5 w-[240px] rounded-2xl border border-white/15 bg-black/80 p-4 shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
            >
              <div className="flex items-center gap-3 mb-3">
                <img src={MUSICIAN} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-white/30" />
                <div>
                  <p className={`${BODY} text-white text-xs font-semibold`}>Alex Rivers</p>
                  <p className={`${BODY} text-white/50 text-[10px]`}>linkmonkey.io/alex</p>
                </div>
              </div>
              {['Latest single', 'Tour dates', 'Merch store'].map((t, i) => (
                <div key={i} className={`${BODY} text-[11px] text-white/80 py-2 px-2.5 rounded-md border border-white/10 bg-white/[0.04] mb-1.5`}>
                  {t}
                </div>
              ))}
            </motion.div>
          </div>
        </BentoCard>

        {/* Brand icons */}
        <BentoCard span="col-span-12 lg:col-span-4" testid="bento-icons">
          <div className="p-7 md:p-10 h-full flex flex-col">
            <Sparkles className="w-5 h-5 text-gray-500 mb-4" strokeWidth={1.5} />
            <h3 className={`${DISPLAY} text-gray-900 text-3xl mb-2 tracking-tight`}>63 brand icons</h3>
            <p className={`${BODY} text-gray-600 text-sm leading-relaxed mb-6`}>Real Spotify green. Real TikTok gradient. Not generic glyphs.</p>
            <div className="grid grid-cols-4 gap-2 mt-auto">
              {[
                { Icon: FaInstagram,  label: 'Instagram',   color: '#E4405F' },
                { Icon: FaTiktok,     label: 'TikTok',      color: '#000000' },
                { Icon: FaYoutube,    label: 'YouTube',     color: '#FF0000' },
                { Icon: FaSpotify,    label: 'Spotify',     color: '#1DB954' },
                { Icon: FaSoundcloud, label: 'SoundCloud',  color: '#FF5500' },
                { Icon: FaDiscord,    label: 'Discord',     color: '#5865F2' },
                { Icon: FaPatreon,    label: 'Patreon',     color: '#FF424D' },
                { Icon: FaTwitch,     label: 'Twitch',      color: '#9146FF' },
                { Icon: SiApplemusic, label: 'Apple Music', color: '#FA243C' },
                { Icon: FaXTwitter,   label: 'X (Twitter)', color: '#FFFFFF' },
                { Icon: FaTelegram,   label: 'Telegram',    color: '#229ED9' },
                { Icon: FaSnapchat,   label: 'Snapchat',    color: '#FFFC00' },
              ].map(({ Icon, label, color }, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-lg flex items-center justify-center transition-transform hover:scale-105"
                  style={{
                    background: color === '#000000' ? '#1a1a1a' : color,
                    boxShadow: `0 0 0 1px rgba(0,0,0,0.08), 0 8px 24px ${color}40`,
                  }}
                >
                  <Icon
                    aria-label={label}
                    role="img"
                    className="w-5 h-5 md:w-6 md:h-6"
                    style={{
                      color: color === '#FFFFFF' ? '#000000'
                           : color === '#FFFC00' ? '#000000'
                           : '#FFFFFF',
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </BentoCard>

        <BentoCard span="col-span-12 sm:col-span-6 lg:col-span-4" testid="bento-fanpage">
          <div className="p-7 relative">
            <Heart className="w-5 h-5 text-gray-500 mb-4" strokeWidth={1.5} />
            <h3 className={`${DISPLAY} text-gray-900 text-2xl mb-2 tracking-tight`}>Fan page</h3>
            <p className={`${BODY} text-gray-600 text-sm leading-relaxed mb-5`}>Bebo-style feed. Posts, photos, replies.</p>
            <div className="flex -space-x-2">
              {[FASHION, MUSICIAN, FASHION, MUSICIAN].map((src,i)=>(
                <div key={i} className="w-9 h-9 rounded-full border-2 border-white overflow-hidden">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
              <div className="w-9 h-9 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center">
                <span className={`${BODY} text-gray-700 text-[10px] font-bold`}>+24</span>
              </div>
            </div>
          </div>
        </BentoCard>

        <BentoCard span="col-span-12 sm:col-span-6 lg:col-span-4" testid="bento-analytics">
          <div className="p-7">
            <BarChart3 className="w-5 h-5 text-gray-500 mb-4" strokeWidth={1.5} />
            <h3 className={`${DISPLAY} text-gray-900 text-2xl mb-2 tracking-tight`}>Analytics</h3>
            <p className={`${BODY} text-gray-600 text-sm leading-relaxed mb-4`}>Where, when, what gets clicked. Country-level map included.</p>
            <div className="flex items-end gap-1 h-12">
              {[40,65,30,80,55,90,45,72,60,85].map((h,i)=>(
                <div key={i} className="flex-1 rounded-sm bg-gradient-to-t from-blue-200 to-blue-500" style={{ height: `${h}%` }} />
              ))}
            </div>
          </div>
        </BentoCard>

        {/* Monetize wide */}
        <BentoCard span="col-span-12 lg:col-span-8" testid="bento-monetize">
          <div className="relative p-7 md:p-10 overflow-hidden">
            <div
              className="absolute -right-20 -top-20 w-[28rem] h-[28rem] pointer-events-none"
              style={{ background: 'radial-gradient(closest-side, rgba(16,185,129,0.10) 0%, transparent 70%)' }}
            />
            <div className="relative flex flex-col md:flex-row md:items-end justify-between gap-8">
              <div className="max-w-md">
                <DollarSignIcon />
                <h3 className={`${DISPLAY} text-gray-900 text-3xl mb-2 tracking-tight`}>Get paid on day one</h3>
                <p className={`${BODY} text-gray-600 text-sm leading-relaxed`}>Tip Jar built in. Direct PayPal payouts from fans. No middleman cut.</p>
              </div>
              <div className="flex flex-col gap-2 md:min-w-[260px]">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-3">
                  <Heart className="w-4 h-4 text-emerald-600" />
                  <span className={`${BODY} text-emerald-800 text-sm flex-1`}>Tip jar</span>
                  <span className={`${BODY} text-emerald-600 text-xs font-mono`}>+€840</span>
                </div>
              </div>
            </div>
          </div>
        </BentoCard>

        <BentoCard span="col-span-12 lg:col-span-4" testid="bento-qr">
          <div className="p-7 flex flex-col h-full">
            <QrCode className="w-5 h-5 text-gray-500 mb-4" strokeWidth={1.5} />
            <h3 className={`${DISPLAY} text-gray-900 text-2xl mb-2 tracking-tight`}>QR codes</h3>
            <p className={`${BODY} text-gray-600 text-sm leading-relaxed`}>Branded QR for flyers, merch and gigs. PNG + SVG downloads.</p>
          </div>
        </BentoCard>

        <BentoCard span="col-span-12 lg:col-span-6" testid="bento-themes">
          <div className="p-7 md:p-10">
            <Palette className="w-5 h-5 text-gray-500 mb-4" strokeWidth={1.5} />
            <h3 className={`${DISPLAY} text-gray-900 text-2xl mb-2 tracking-tight`}>17 themes · 12 fonts · 11 wallpapers</h3>
            <p className={`${BODY} text-gray-600 text-sm leading-relaxed mb-5`}>Or build your own with custom accent colours and animations.</p>
            <div className="grid grid-cols-6 gap-1.5">
              {['#FF6B6B','#FFD93D','#6BCB77','#4D96FF','#9B5DE5','#FF8FAB','#00BBF9','#FEE440','#F15BB5','#06D6A0','#118AB2','#FF9F1C'].map((c,i)=>(
                <div key={i} className="aspect-square rounded-md" style={{ background: c, boxShadow: `0 0 0 1px ${c}55, 0 4px 12px ${c}30` }} />
              ))}
            </div>
          </div>
        </BentoCard>

        <BentoCard span="col-span-12 lg:col-span-6" testid="bento-embeds">
          <div className="p-7 md:p-10">
            <Music2 className="w-5 h-5 text-gray-500 mb-4" strokeWidth={1.5} />
            <h3 className={`${DISPLAY} text-gray-900 text-2xl mb-2 tracking-tight`}>Rich embeds</h3>
            <p className={`${BODY} text-gray-600 text-sm leading-relaxed mb-5`}>YouTube, Spotify, SoundCloud, Apple Music — auto-detected and played inline.</p>
            <div className="flex gap-2 flex-wrap">
              {['YouTube','Spotify','SoundCloud','Apple Music','Bandcamp','TikTok'].map(t=>(
                <span key={t} className={`${BODY} text-xs text-gray-600 px-3 py-1.5 rounded-full border border-gray-200 bg-[#D9E9FC]`}>{t}</span>
              ))}
            </div>
          </div>
        </BentoCard>
      </div>
    </div>
  </section>
);

const DollarSignIcon = () => (
  <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
    <span className={`${DISPLAY} text-emerald-600 text-lg`}>€</span>
  </div>
);

/* ---------- PRICING teaser ---------- */
const PricingTeaser = () => (
  <section className="relative py-28 md:py-40 px-6 md:px-10 overflow-hidden bg-black" data-testid="landing-pricing-teaser">
    <div
      className="absolute inset-0 -z-10 opacity-60"
      style={{ backgroundImage: `url(${PRICING_STAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
    />
    <div className="absolute inset-0 -z-10 bg-gradient-to-b from-black via-transparent to-black" />

    <div className="relative max-w-4xl mx-auto text-center">
      <Reveal>
        <p className={`${BODY} text-[11px] uppercase tracking-[0.25em] text-white/40 mb-5`}>Pricing</p>
        <h2 className={`${DISPLAY} text-[clamp(2.5rem,7vw,6rem)] leading-[0.95] mb-6`}>
          <span className={METALLIC_DARKBG}>€4/month.</span><br />
          <span className="italic font-light text-[#3377FF]" style={{ textShadow: '0 0 40px rgba(0,85,255,0.5)' }}>That's it.</span>
        </h2>
        <p className={`${BODY} text-white/60 text-lg max-w-xl mx-auto mb-10`}>
          Free forever for the essentials. 7-day Pro trial — no card. €40/yr saves you 17%.
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <Link to="/pricing">
          <button
            className={`${BODY} inline-flex items-center gap-2 bg-[#0055FF] text-white text-sm font-semibold px-7 py-4 rounded-full hover:bg-[#3377FF] transition-all shadow-[0_10px_40px_rgba(0,85,255,0.45)] hover:shadow-[0_15px_60px_rgba(0,85,255,0.7)] hover:scale-[1.02]`}
            data-testid="pricing-teaser-cta">
            Compare plans
            <ArrowRight className="w-4 h-4" />
          </button>
        </Link>
      </Reveal>
    </div>
  </section>
);

/* ---------- AFFILIATE strip removed from public landing — invite-only program. ---------- */

/* ---------- COMPARISON vs Linktree ---------- */
const HomeComparison = () => (
  <section className="relative py-24 md:py-32 px-6 md:px-10 bg-[#F1F7FF]" data-testid="landing-comparison">
    <div className="max-w-4xl mx-auto">
      <Reveal>
        <p className={`${BODY} text-[11px] uppercase tracking-[0.25em] text-gray-500 mb-4`}>Switching?</p>
        <h2 className={`${DISPLAY} text-[clamp(2rem,5vw,4rem)] leading-[0.95] mb-10`}>
          <span className={METALLIC}>LinkMonkey vs Linktree.</span><br />
          <span className="text-gray-500">Side by side.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <ComparisonTable competitor="Linktree" rows={LINKTREE_ROWS} />
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-8 flex flex-col sm:flex-row items-center gap-4">
          <Link to="/signup">
            <button className={`${BODY} inline-flex items-center gap-2 bg-[#0055FF] text-white text-sm font-semibold px-7 py-4 rounded-full hover:bg-[#3377FF] transition-all shadow-[0_10px_40px_rgba(0,85,255,0.45)] hover:scale-[1.02]`}
              data-testid="comparison-cta">
              Switch to LinkMonkey
              <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
          <Link to="/linktree-alternative" className={`${BODY} text-sm text-gray-600 hover:text-gray-900 transition-colors`}>
            Read the full comparison →
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------- HOMEPAGE FAQ ---------- */
const HOME_FAQS = [
  { q: 'Is LinkMonkey free?', a: 'Yes — the free plan is free forever and includes your public page, unlimited links and real brand icons. Pro unlocks everything for €4/mo or €40/yr (€3.33/mo), with a 7-day trial and no card required.' },
  { q: 'What is a link-in-bio tool?', a: 'A link-in-bio tool gives you one shareable page (like linkmonkey.io/yourname) that holds all your important links — music, videos, shops, socials — so you only ever need one URL in your Instagram, TikTok or YouTube bio.' },
  { q: 'How is LinkMonkey different from Linktree?', a: 'The features Linktree gates behind higher tiers — rich embeds, analytics, QR codes, themes, tip jar — are all included in one simple LinkMonkey Pro plan from €3.33/mo. There is also a free plan forever.' },
  { q: 'Can I accept tips from fans?', a: 'Yes. The built-in tip jar pays out directly to your PayPal, and LinkMonkey takes 0% commission.' },
  { q: 'Can I track clicks and visitors?', a: 'Yes — page views, clicks per link and a country-level visitor map are included with Pro.' },
  { q: 'Can I create a QR code for my page?', a: 'Yes, generate branded QR codes in PNG and SVG for flyers, merch, posters and counters.' },
  { q: 'Can I customise my page?', a: 'Completely — 17 themes, 12 fonts, 11 wallpapers, animations, button styles and custom accent colours.' },
  { q: 'Can I move from another link-in-bio tool?', a: 'Yes. Create a free account, add your links (platforms are auto-detected), and swap the URL in your bios. Most creators switch in under 5 minutes.' },
];

/* ---------- MASSIVE FOOTER ---------- */
export const Footer = () => (
  <footer className="relative bg-[#06070C] pt-20 md:pt-28 pb-10 px-6 md:px-10 overflow-hidden" data-testid="landing-footer">
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start gap-12 md:gap-20 mb-16">
        <div className="max-w-sm">
          <div className="flex items-center gap-3 mb-5">
            <MonkeyLogo size={32} />
            <span className={`${BODY} text-base font-semibold text-white`}>LinkMonkey</span>
          </div>
          <p className={`${BODY} text-white/50 text-sm leading-relaxed`}>
            One link in bio. For musicians, models, makers and the misfits in between.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-10 md:gap-14">
          <div>
            <p className={`${BODY} text-[10px] uppercase tracking-[0.25em] text-white/30 mb-4`}>Product</p>
            <div className="space-y-3 text-sm">
              <Link to="/signup" className={`${BODY} block text-white/70 hover:text-white transition-colors`}>Sign up</Link>
              <Link to="/login" className={`${BODY} block text-white/70 hover:text-white transition-colors`}>Log in</Link>
              <Link to="/pricing" className={`${BODY} block text-white/70 hover:text-white transition-colors`}>Pricing</Link>
              <Link to="/examples" className={`${BODY} block text-white/70 hover:text-white transition-colors`} data-testid="footer-examples-link">Examples</Link>
              <Link to="/free-link-in-bio" className={`${BODY} block text-white/70 hover:text-white transition-colors`}>Free link in bio</Link>
              <Link to="/about" className={`${BODY} block text-white/70 hover:text-white transition-colors`} data-testid="footer-about-link">About</Link>
              <Link to="/reviews" className={`${BODY} block text-white/70 hover:text-white transition-colors`} data-testid="footer-reviews-link">Reviews</Link>
            </div>
          </div>
          <div>
            <p className={`${BODY} text-[10px] uppercase tracking-[0.25em] text-white/30 mb-4`}>Use cases</p>
            <div className="space-y-3 text-sm">
              <Link to="/link-in-bio-for-musicians" className={`${BODY} block text-white/70 hover:text-white transition-colors`}>For musicians</Link>
              <Link to="/link-in-bio-for-streamers" className={`${BODY} block text-white/70 hover:text-white transition-colors`}>For streamers</Link>
              <Link to="/link-in-bio-for-artists" className={`${BODY} block text-white/70 hover:text-white transition-colors`}>For artists</Link>
              <Link to="/link-in-bio-for-podcasters" className={`${BODY} block text-white/70 hover:text-white transition-colors`}>For podcasters</Link>
              <Link to="/link-in-bio-for-small-business" className={`${BODY} block text-white/70 hover:text-white transition-colors`}>For small business</Link>
            </div>
          </div>
          <div>
            <p className={`${BODY} text-[10px] uppercase tracking-[0.25em] text-white/30 mb-4`}>Compare</p>
            <div className="space-y-3 text-sm">
              <Link to="/linktree-alternative" className={`${BODY} block text-white/70 hover:text-white transition-colors`} data-testid="footer-linktree-alt-link">Linktree alternative</Link>
              <Link to="/beacons-alternative" className={`${BODY} block text-white/70 hover:text-white transition-colors`}>Beacons alternative</Link>
              <Link to="/carrd-alternative" className={`${BODY} block text-white/70 hover:text-white transition-colors`}>Carrd alternative</Link>
              <Link to="/stan-store-alternative" className={`${BODY} block text-white/70 hover:text-white transition-colors`}>Stan Store alternative</Link>
              <Link to="/features/tip-jar" className={`${BODY} block text-white/70 hover:text-white transition-colors`}>Tip jar</Link>
              <Link to="/features/analytics" className={`${BODY} block text-white/70 hover:text-white transition-colors`}>Analytics</Link>
            </div>
          </div>
          <div>
            <p className={`${BODY} text-[10px] uppercase tracking-[0.25em] text-white/30 mb-4`}>Legal</p>
            <div className="space-y-3 text-sm">
              <Link to="/terms" className={`${BODY} block text-white/70 hover:text-white transition-colors`} data-testid="footer-terms-link">Terms of Service</Link>
              <Link to="/privacy" className={`${BODY} block text-white/70 hover:text-white transition-colors`} data-testid="footer-privacy-link">Privacy Policy</Link>
              <Link to="/dmca" className={`${BODY} block text-white/70 hover:text-white transition-colors`} data-testid="footer-dmca-link">DMCA</Link>
              <a href="mailto:hello@linkmonkey.io" className={`${BODY} block text-white/70 hover:text-white transition-colors`}>hello@linkmonkey.io</a>
            </div>
          </div>
        </div>
      </div>

      {/* Massive wordmark */}
      <div className="relative -mb-2 select-none pointer-events-none">
        <h2 className={`${DISPLAY} text-white/[0.06] leading-[0.85] text-[20vw] whitespace-nowrap`}>
          LINKMONKEY
        </h2>
        <motion.img
          src={MASCOT_3D}
          alt="LinkMonkey mascot"
          initial={{ y: 0 }}
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 6, ease: 'easeInOut', repeat: Infinity }}
          className="absolute right-[4%] bottom-[10%] w-[18vw] max-w-[200px] min-w-[100px] drop-shadow-[0_30px_50px_rgba(0,85,255,0.3)]"
        />
      </div>

      <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-3">
        <p className={`${BODY} text-xs text-white/40`}>© {new Date().getFullYear()} LinkMonkey. All rights reserved.</p>
        <p className={`${BODY} text-xs text-white/40 font-mono`}>v2.0</p>
      </div>
    </div>
  </footer>
);

/* ---------- NAV ---------- */
export const Nav = () => (
  <nav className="fixed top-0 inset-x-0 z-50 border-b border-gray-200 bg-white/90">
    <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
        <MonkeyLogo size={28} />
        <span className={`${BODY} text-[15px] font-semibold tracking-tight text-gray-950`}>LinkMonkey</span>
      </Link>

      <div className={`${BODY} hidden md:flex items-center gap-8 text-[13px] text-gray-600`}>
        <a href="/#features" className="hover:text-gray-950 transition-colors">Features</a>
        <Link to="/pricing" className="hover:text-gray-950 transition-colors">Pricing</Link>
        <Link to="/examples" className="hover:text-gray-950 transition-colors" data-testid="nav-examples-link">Examples</Link>
      </div>

      <div className="flex items-center gap-2">
        <Link to="/login">
          <button className={`${BODY} text-[13px] font-medium text-gray-600 hover:text-gray-950 px-3.5 py-2 transition-colors`}
            data-testid="login-btn">Log in</button>
        </Link>
        <Link to="/signup">
          <button className={`${BODY} text-[13px] font-semibold bg-gray-950 text-white px-4 py-2 rounded-full hover:bg-gray-800 transition-colors hover:scale-[1.03] whitespace-nowrap`}
            data-testid="signup-btn">Get started</button>
        </Link>
      </div>
    </div>
  </nav>
);

const LandingPage = () => {
  useLenis();

  useEffect(() => {
    try {
      if (!sessionStorage.getItem('lm_home_visit')) {
        sessionStorage.setItem('lm_home_visit', '1');
        api.trackSiteVisit('home').catch(() => {});
      }
    } catch (_) {}
  }, []);

  return (
    <div className="min-h-screen bg-[#D9E9FC] text-gray-950 antialiased selection:bg-gray-950 selection:text-white" data-testid="landing-page">
      <Helmet>
        <title>LinkMonkey — Free Link in Bio Tool for Creators | Linktree Alternative</title>
        <meta name="description" content="Create a free link-in-bio page with rich embeds, 63 real brand icons, tip jar, fan page, analytics and QR codes. The affordable Linktree alternative — Pro from €3.33/mo." />
        <link rel="canonical" href="https://linkmonkey.io/" />
        <link rel="preconnect" href="https://api.fontshare.com" crossOrigin="anonymous" />
        <link
          href="https://api.fontshare.com/v2/css?f[]=cabinet-grotesk@400,500,700,800,900,400i&f[]=satoshi@400,500,700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <Nav />
      <Hero />
      <KineticMarquee />
      <Features />
      <HomeComparison />
      <PricingTeaser />
      <FaqSection faqs={HOME_FAQS} heading="Questions, answered." />
      <Footer />
    </div>
  );
};

export default LandingPage;
